
import { GalleryImage } from "@/components/index/gallery/galleryData";
import { mockGalleryImages } from "@/data/mockGalleryImages";
import { ServiceResponse } from "./types";

// Simulate API delay
const simulateDelay = (ms: number = 300) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Get all gallery images
 */
export const getAllGalleryImages = async (): Promise<ServiceResponse<GalleryImage[]>> => {
  try {
    await simulateDelay();
    return { data: mockGalleryImages };
  } catch (error) {
    console.error("Error fetching gallery images:", error);
    return { data: [], error: "Failed to fetch gallery images" };
  }
};

/**
 * Get gallery image by index
 */
export const getGalleryImageByIndex = async (index: number): Promise<ServiceResponse<GalleryImage | null>> => {
  try {
    await simulateDelay();
    if (index < 0 || index >= mockGalleryImages.length) {
      return { data: null, error: "Gallery image not found" };
    }
    return { data: mockGalleryImages[index] };
  } catch (error) {
    console.error(`Error fetching gallery image at index ${index}:`, error);
    return { data: null, error: "Failed to fetch gallery image" };
  }
};

/**
 * Get gallery image by ID
 */
export const getGalleryImageById = async (id: string): Promise<ServiceResponse<GalleryImage | null>> => {
  try {
    await simulateDelay();
    const image = mockGalleryImages.find(img => img.id === id);
    if (!image) {
      return { data: null, error: "Gallery image not found" };
    }
    return { data: image };
  } catch (error) {
    console.error(`Error fetching gallery image with ID ${id}:`, error);
    return { data: null, error: "Failed to fetch gallery image" };
  }
};

/**
 * Get gallery images by category
 */
export const getGalleryImagesByCategory = async (category: string): Promise<ServiceResponse<GalleryImage[]>> => {
  try {
    await simulateDelay();
    const filteredImages = mockGalleryImages.filter(image => image.category === category);
    return { data: filteredImages };
  } catch (error) {
    console.error(`Error fetching gallery images for category ${category}:`, error);
    return { data: [], error: "Failed to fetch gallery images" };
  }
};

/**
 * Get available gallery categories
 */
export const getGalleryCategories = async (): Promise<ServiceResponse<string[]>> => {
  try {
    await simulateDelay();
    const categories = Array.from(new Set(mockGalleryImages
      .filter(image => image.category) // Filter out undefined categories
      .map(image => image.category as string))); // Cast to string as we filtered out undefined
    return { data: categories };
  } catch (error) {
    console.error("Error fetching gallery categories:", error);
    return { data: [], error: "Failed to fetch gallery categories" };
  }
};
